import { useParams, Link } from 'react-router-dom';
import { services } from '../../../mocks/services';
import RevealOnScroll from '../../../components/base/RevealOnScroll';

const PHONE = '(708) 698-4072';
const PHONE_HREF = 'tel:7086984072';

const processSteps = [
  { label: 'Free Inspection', icon: 'ri-search-eye-line' },
  { label: 'Written Estimate', icon: 'ri-file-text-line' },
  { label: 'Material Selection', icon: 'ri-palette-line' },
  { label: 'Expert Installation', icon: 'ri-hammer-line' },
  { label: 'Final Walkthrough', icon: 'ri-checkbox-circle-line' },
];

export default function ServiceDetailPage() {
  const { slug } = useParams<{ slug: string }>();
  const service = services.find((s) => s.slug === slug);

  if (!service) {
    return (
      <main className="min-h-screen flex items-center justify-center bg-brand-cream">
        <div className="text-center">
          <h1 className="font-display text-3xl font-bold text-brand-dark mb-3">Service Not Found</h1>
          <Link to="/services" className="text-brand-green font-semibold hover:underline">Back to Services</Link>
        </div>
      </main>
    );
  }

  const relatedServices = services.filter((s) => s.id !== service.id && s.category === service.category).slice(0, 3);

  return (
    <main>
      {/* Hero */}
      <section
        className="relative flex items-end min-h-[420px] pt-40 pb-16 overflow-hidden"
        style={{ backgroundImage: `url('${service.image}')`, backgroundSize: 'cover', backgroundPosition: 'center 30%' }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/60 to-black/25" />
        <div className="absolute inset-0 bg-gradient-to-r from-black/60 to-transparent" />
        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <nav className="flex items-center gap-2 text-xs text-white/40 mb-4">
            <Link to="/" className="hover:text-white cursor-pointer">Home</Link>
            <i className="ri-arrow-right-s-line" />
            <Link to="/services" className="hover:text-white cursor-pointer">Services</Link>
            <i className="ri-arrow-right-s-line" />
            <span className="text-white/75">{service.name}</span>
          </nav>
          <div className="inline-flex items-center gap-2 bg-brand-gold/20 border border-brand-gold/40 rounded-full px-4 py-1.5 mb-4">
            <i className={`${service.icon} text-brand-gold text-xs`} />
            <span className="text-brand-gold text-xs font-bold uppercase tracking-widest">Chicago {service.name}</span>
          </div>
          <h1 className="font-display text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
            {service.name}<br />
            <span className="text-brand-gold italic">in Chicago, IL</span>
          </h1>
          <p className="text-white/75 max-w-xl leading-relaxed mb-7">{service.shortDesc}</p>
          <div className="flex flex-col sm:flex-row gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-8 py-3.5 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Get Free {service.name} Estimate
            </Link>
            <a href={PHONE_HREF} className="border-2 border-white/40 hover:border-white text-white text-sm font-bold px-8 py-3.5 rounded-md transition-all flex items-center gap-2 cursor-pointer whitespace-nowrap">
              <i className="ri-phone-fill text-brand-gold" />
              Call {PHONE}
            </a>
          </div>
        </div>
      </section>

      {/* Trust bar */}
      <section className="bg-brand-dark py-5 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex flex-wrap items-center justify-center gap-6 lg:gap-10">
          {['Trusted Local Experts', 'Free Written Estimates', 'Licensed & Insured', 'Chicago-Area Specialists'].map((badge) => (
            <span key={badge} className="flex items-center gap-1.5 text-xs text-gray-400 whitespace-nowrap">
              <i className="ri-shield-check-fill text-brand-gold text-xs" />
              {badge}
            </span>
          ))}
        </div>
      </section>

      {/* Main content */}
      <section className="bg-brand-cream py-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Left main */}
            <div className="lg:col-span-2 flex flex-col gap-8">

              {/* Services overview */}
              <RevealOnScroll>
                <div className="bg-white rounded-xl p-8 border border-brand-cream-3">
                  <h2 className="font-display text-2xl font-bold text-brand-dark mb-4">
                    What&apos;s Included in Our {service.name}
                  </h2>
                  <p className="text-gray-600 leading-relaxed mb-7">{service.longDesc}</p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.features.map((feature) => (
                      <div key={feature} className="flex items-center gap-2.5">
                        <div className="w-5 h-5 flex items-center justify-center bg-brand-green/10 rounded-full flex-shrink-0">
                          <i className="ri-check-line text-brand-green text-xs" />
                        </div>
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>

              {/* AI Answer Block */}
              <RevealOnScroll delay={80}>
                <div className="bg-white rounded-xl p-8 border border-brand-cream-3">
                  <h2 className="font-display text-xl font-bold text-brand-dark mb-6">
                    {service.name} — Frequently Asked Questions
                  </h2>
                  <div className="flex flex-col gap-5">
                    {[
                      {
                        q: `What is ${service.name}?`,
                        a: `${service.name} is a professional exterior service designed to protect and enhance your Chicago property. ${service.longDesc}`,
                      },
                      {
                        q: `What does ${service.name} include?`,
                        a: `It includes a complete inspection, planning, professional material selection, and expert installation. Our services cover: ${service.features.slice(0, 5).join(', ')}, and more.`,
                      },
                      {
                        q: `Why is ${service.name} important for Chicago properties?`,
                        a: "Chicago's extreme weather — heavy snowfall, freeze-thaw cycles, and summer heat — demands proper construction and materials. Professional service ensures long-term protection, durability, and maintained property value.",
                      },
                      {
                        q: `How long does ${service.name} take?`,
                        a: 'Most projects are completed within one to several days depending on scope and complexity. We provide a firm timeline in your written estimate before work begins.',
                      },
                      {
                        q: `How much does ${service.name} cost in Chicago?`,
                        a: 'Pricing depends on project size, materials selected, and scope of work. We provide detailed written estimates at no charge after a free on-site inspection. No hidden fees.',
                      },
                    ].map((item, idx) => (
                      <div key={idx} className="border-b border-brand-cream-2 pb-5 last:border-0 last:pb-0">
                        <h3 className="text-sm font-bold text-brand-dark mb-2 flex items-start gap-2">
                          <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                          {item.q}
                        </h3>
                        <p className="text-sm text-gray-600 leading-relaxed pl-5">{item.a}</p>
                      </div>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>

              {/* Process */}
              <RevealOnScroll delay={120}>
                <div className="bg-brand-dark rounded-xl p-8 relative overflow-hidden">
                  <div className="absolute inset-0 opacity-[0.04]" style={{ backgroundImage: 'repeating-linear-gradient(45deg, #c9a227 0, #c9a227 1px, transparent 0, transparent 20px)', backgroundSize: '28px 28px' }} />
                  <h2 className="font-display text-xl font-bold text-white mb-6 relative">Our Process</h2>
                  <div className="flex flex-col gap-3 relative">
                    {processSteps.map((step, idx) => (
                      <div key={step.label} className="flex items-center gap-4 bg-white/5 border border-white/10 rounded-lg px-4 py-3">
                        <div className="w-8 h-8 flex items-center justify-center bg-brand-gold/15 border border-brand-gold/25 rounded-full flex-shrink-0">
                          <span className="text-brand-gold text-xs font-bold">{String(idx + 1).padStart(2, '0')}</span>
                        </div>
                        <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                          <i className={`${step.icon} text-brand-gold text-sm`} />
                        </div>
                        <span className="text-white text-sm font-medium">{step.label}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </RevealOnScroll>
            </div>

            {/* Sidebar */}
            <div className="flex flex-col gap-5">
              {/* CTA card */}
              <RevealOnScroll direction="right">
                <div className="bg-brand-green rounded-xl p-7">
                  <h3 className="font-display text-xl font-bold text-white mb-2">Ready to Get Started?</h3>
                  <p className="text-white/75 text-sm mb-6 leading-relaxed">
                    Schedule your free inspection today and receive a detailed written estimate.
                  </p>
                  <Link
                    to="/contact"
                    className="block bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-5 py-3.5 rounded-lg text-center transition-colors cursor-pointer whitespace-nowrap mb-3"
                  >
                    Request Free Estimate
                  </Link>
                  <a
                    href={PHONE_HREF}
                    className="block border-2 border-white/35 hover:border-white text-white text-sm font-bold px-5 py-3.5 rounded-lg text-center transition-all cursor-pointer whitespace-nowrap flex items-center justify-center gap-2"
                  >
                    <i className="ri-phone-fill" />
                    {PHONE}
                  </a>
                </div>
              </RevealOnScroll>

              {/* Why Choose Us */}
              <RevealOnScroll direction="right" delay={80}>
                <div className="bg-white rounded-xl p-6 border border-brand-cream-3">
                  <h3 className="font-bold text-brand-dark mb-4 text-xs uppercase tracking-widest">Why Green World</h3>
                  <ul className="flex flex-col gap-3">
                    {['Family-owned since 1989', 'Licensed & fully insured', 'Free written estimates', 'Residential & commercial', 'Chicago climate expertise', 'Transparent communication'].map((item) => (
                      <li key={item} className="flex items-center gap-2 text-sm text-gray-600">
                        <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                          <i className="ri-check-double-line text-brand-green text-sm" />
                        </div>
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>
              </RevealOnScroll>

              {/* Service Areas */}
              <RevealOnScroll direction="right" delay={120}>
                <div className="bg-brand-cream rounded-xl p-6 border border-brand-cream-3">
                  <h3 className="font-bold text-brand-dark mb-3 text-xs uppercase tracking-widest">Service Areas</h3>
                  <p className="text-xs text-gray-600 leading-relaxed mb-3">
                    Serving Chicago and 24+ suburbs including Evanston, Skokie, Oak Park, Park Ridge, Berwyn,
                    Elmhurst, and more.
                  </p>
                  <Link to="/locations" className="text-xs font-bold text-brand-green flex items-center gap-1 hover:underline cursor-pointer">
                    View All Locations <i className="ri-arrow-right-line text-xs" />
                  </Link>
                </div>
              </RevealOnScroll>

              {/* Related services */}
              {relatedServices.length > 0 && (
                <RevealOnScroll direction="right" delay={160}>
                  <div className="bg-white rounded-xl p-6 border border-brand-cream-3">
                    <h3 className="font-bold text-brand-dark mb-4 text-xs uppercase tracking-widest">Related Services</h3>
                    <div className="flex flex-col gap-3">
                      {relatedServices.map((s) => (
                        <Link key={s.id} to={`/services/${s.slug}`} className="flex items-center gap-3 group cursor-pointer">
                          <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0">
                            <img src={s.image} alt={s.name} className="w-full h-full object-cover object-top" />
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-brand-dark group-hover:text-brand-green transition-colors">{s.name}</div>
                            <div className="text-xs text-gray-500 truncate">{s.shortDesc.slice(0, 50)}...</div>
                          </div>
                          <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                            <i className="ri-arrow-right-line text-brand-green text-xs group-hover:translate-x-1 transition-transform" />
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                </RevealOnScroll>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="bg-white py-14">
        <div className="max-w-7xl mx-auto px-6 lg:px-12">
          <RevealOnScroll className="text-center mb-8">
            <h2 className="font-display text-2xl font-bold text-brand-dark">Precision Execution. High-Quality Materials. Reliable Results.</h2>
          </RevealOnScroll>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {['Local expertise', 'Reliable execution', 'High-end results', 'Transparent pricing'].map((item, idx) => (
              <RevealOnScroll key={item} delay={idx * 60}>
                <div className="bg-brand-cream rounded-xl p-5 text-center border border-brand-cream-3">
                  <i className="ri-star-fill text-brand-gold text-xl mb-2 block" />
                  <span className="text-sm font-semibold text-brand-dark">{item}</span>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="bg-brand-cream py-10">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <RevealOnScroll>
            <h2 className="font-display text-xl font-bold text-brand-dark mb-3">
              {service.name} Available Across Illinois
            </h2>
            <p className="text-gray-600 text-sm mb-4">
              Serving Chicago and 24+ Chicagoland suburbs with localized service coverage.
            </p>
            <Link to="/locations" className="inline-flex items-center gap-1.5 text-brand-green font-bold text-sm hover:underline cursor-pointer">
              View All Service Areas <i className="ri-arrow-right-line text-xs" />
            </Link>
          </RevealOnScroll>
        </div>
      </section>

      {/* Testimonials */}
      <section className="bg-brand-dark py-12">
        <div className="max-w-4xl mx-auto px-6 lg:px-12 text-center">
          <RevealOnScroll>
            <h2 className="font-display text-2xl font-bold text-white mb-6">Consistent positive feedback on quality, durability, and professionalism.</h2>
            <div className="flex items-center justify-center gap-1 mb-2">
              {Array.from({ length: 5 }).map((_, i) => <i key={i} className="ri-star-fill text-brand-gold text-lg" />)}
            </div>
            <p className="text-gray-400 text-sm">5.0 rating across Google, Yelp, and HomeAdvisor</p>
          </RevealOnScroll>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream py-14">
        <div className="max-w-4xl mx-auto px-6 lg:px-12">
          <RevealOnScroll>
            <h2 className="font-display text-2xl font-bold text-brand-dark mb-6">{service.name} FAQ</h2>
          </RevealOnScroll>
          <div className="flex flex-col gap-4">
            {[
              { q: `Who offers ${service.name} near me?`, a: `Green World Construction provides ${service.name} across Chicago and 24+ Chicagoland suburbs with a focus on quality, transparency, and long-term results. Family-owned since 1989.` },
              { q: `How much does ${service.name} cost?`, a: `Pricing depends on project scope, materials, and customization. We provide detailed written estimates at no charge after a free on-site inspection.` },
              { q: `How long does ${service.name} take?`, a: `Most ${service.name} projects are completed within one to several days. Larger or more complex builds may require additional time. Firm timelines are included in every written estimate.` },
            ].map((item, idx) => (
              <RevealOnScroll key={idx} delay={idx * 70}>
                <div className="bg-white rounded-xl p-6 border border-brand-cream-3">
                  <h3 className="font-bold text-brand-dark mb-3 text-sm flex items-start gap-2">
                    <i className="ri-question-line text-brand-green text-sm mt-0.5 flex-shrink-0" />
                    {item.q}
                  </h3>
                  <p className="text-sm text-gray-600 leading-relaxed">{item.a}</p>
                </div>
              </RevealOnScroll>
            ))}
          </div>
        </div>
      </section>

      {/* Bottom CTA */}
      <section className="bg-brand-dark py-16">
        <RevealOnScroll className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="font-display text-3xl font-bold text-white mb-3">Start Your {service.name} Project Today</h2>
          <p className="text-gray-400 mb-8">Request a free consultation and get a customized plan from Chicago&apos;s roofing experts since 1989.</p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <Link to="/contact" className="bg-brand-gold hover:bg-brand-gold-dark text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap">
              Get Free Inspection
            </Link>
            <a href={PHONE_HREF} className="border-2 border-white/30 hover:border-white text-white text-sm font-bold px-10 py-4 rounded-md transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
              <i className="ri-phone-fill" />
              {PHONE}
            </a>
          </div>
        </RevealOnScroll>
      </section>
    </main>
  );
}
